
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import InputSwitch from 'primevue/inputswitch';
import Dropdown from 'primevue/dropdown';
import AmountStepper from '@/components/billing/dialogs/AmountStepper.vue';
import { fetchSmsPricing } from '@/composables/billing/billingApi';
import { SmsPricingResponse } from '@/types/billing-types';

const STEP_VALUES = [
  10, 25, 50, 75, 100, 125, 150, 200,
  500, 1000, 2000, 3000, 5000, 10000,
];

const FLAGS: Record<string, string> = {
  FR: '🇫🇷',
  BE: '🇧🇪',
  ES: '🇪🇸',
  IT: '🇮🇹',
  GB: '🇬🇧',
  US: '🇺🇸',
};

export default defineComponent({
  name: 'AddCreditsStep',

  components: { InputSwitch, Dropdown, AmountStepper },

  props: {
    modelValue: {
      type: Number as PropType<number>,
      default: 0,
    },
    currency: {
      type: String as PropType<'EUR' | 'USD'>,
      default: 'EUR',
    },
    /**
     * Plan that the shop is about to switch to in the current wizard flow
     * (passed by ChangePlanDialog from its selectedPlan ref). When set, the
     * SMS pricing grid is fetched with this tier instead of the shop's CURRENT
     * plan — otherwise the « tarif à l'unité » displayed (and the « X %
     * d'économie » derived from it) would reflect the wrong tier.
     */
    targetPlan: {
      type: String as PropType<string | null>,
      default: null,
    },
    /**
     * Monthly SMS amount the shop is currently subscribed to (`plan.smsPlan`
     * mirrored from the overview). When > 0 and `modelValue` is 0 (no value
     * yet picked by the user), the toggle opens activated and the amount is
     * pre-filled. The customer can then keep / decrease / disable / increase.
     * Spec V4-d & SMS rule: no prorata, only the positive delta is invoiced.
     */
    initialSmsInclusion: {
      type: Number as PropType<number>,
      default: 0,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const smsPricing = ref<SmsPricingResponse | null>(null);
    // Pre-fill from `initialSmsInclusion` when the wizard arrives in this
    // phase with no user-picked value yet (modelValue still 0). Keeps the
    // user's choice if they've already interacted with the toggle.
    const initialFromCurrent = props.modelValue === 0 && props.initialSmsInclusion > 0
      ? props.initialSmsInclusion
      : 0;
    if (initialFromCurrent > 0) {
      emit('update:modelValue', initialFromCurrent);
    }
    const enabled = ref(props.modelValue > 0 || initialFromCurrent > 0);
    // Default to the shop's home country (read from the overview) before
    // falling back to FR. The dropdown remains user-driven afterwards.
    const selectedCountry = ref<string>(
      store.state.billing.overview?.shop?.country ?? 'FR',
    );

    const currencySymbol = computed(() => (
      props.currency === 'USD' ? '$' : '€'
    ));

    const countryOptions = computed(() => (
      smsPricing.value?.countries.map((c) => ({
        value: c.country,
        label: c.countryLabel,
      })) ?? []
    ));

    const selectedCountryData = computed(() => (
      smsPricing.value?.countries.find((c) => c.country === selectedCountry.value) ?? null
    ));

    const estimatedQuantity = computed(() => {
      const c = selectedCountryData.value;
      if (!c || !props.modelValue || c.unitPriceInPlan <= 0) return 0;
      return Math.floor(props.modelValue / c.unitPriceInPlan);
    });

    const flagEmoji = (code: string) => FLAGS[code] ?? '🌐';

    const countryLabel = (code: string) => (
      smsPricing.value?.countries.find((c) => c.country === code)?.countryLabel ?? code
    );

    const formatUnitPrice = (n: number) => {
      const trimmed = n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
      return `${trimmed} ${currencySymbol.value}`;
    };

    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);

    const onAmountChange = (v: number | null | undefined) => {
      const value = v == null || v < 0 ? 0 : Number(v);
      emit('update:modelValue', value);
    };

    const onCountryChange = (v: string) => {
      selectedCountry.value = v;
    };

    const onToggle = (v: boolean) => {
      enabled.value = v;
      if (!v) emit('update:modelValue', 0);
      else if (props.modelValue === 0) emit('update:modelValue', 25);
    };

    // Refetch pricing whenever the wizard-selected plan changes (or on
    // initial mount when no targetPlan is provided). `immediate: true` covers
    // both cases without a separate onMounted hook.
    watch(
      () => props.targetPlan,
      async (plan) => {
        smsPricing.value = await fetchSmsPricing(
          plan ? { planOverride: plan } : {},
        );
      },
      { immediate: true },
    );

    return {
      t,
      STEP_VALUES,
      smsPricing,
      enabled,
      selectedCountry,
      currencySymbol,
      countryOptions,
      selectedCountryData,
      estimatedQuantity,
      flagEmoji,
      countryLabel,
      formatUnitPrice,
      formatNumber,
      onAmountChange,
      onCountryChange,
      onToggle,
    };
  },
});
